import { CarnivalTreasureResult } from '@/models/games/carnivalTreasure';
import { GameType as GameTypeEnum } from '@/models/games/enums/GameType';
import { BetRecord, Summary } from '@/modules/report/slice/betSummary';
import { convertDetailResultString } from '@/utils/games/carnivalTreasure';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    Box,
    IconButton,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AndarBaharGameResult } from './games/AndarBaharGameResult';
import { BaccGameResult } from './games/BaccGameResult';
import { Blackjack2GameResult } from './games/Blackjack2GameResult';
import { BlackjackGameResult } from './games/BlackjackGameResult';
import { CarnivalTreasureGameResult } from './games/CarnivalTreasureGameResult';
import { DragonTigerGameResult } from './games/DragonTigerGameResult';
import { FishPrawnCrabGameResult } from './games/FishPrawnCrabGameResult';
import { PokDengGameResult } from './games/PokDengGameResult';
import { RouletteGameResult } from './games/RouletteGameResult';
import { SedieGameResult } from './games/SedieGameResult';
import { SicBoGameResult } from './games/SicBoGameResult';
import { TeenPattiGameResult } from './games/TeenPattiGameResult';
import { UltraRouletteGameResult } from './games/UltraRouletteGameResult';
import './index.scss';

export type GameResultCellProps = {
    summary: Summary;
    expanded?: boolean;
};

export type BJGameResultCellProps = GameResultCellProps & {
    betRecords?: Array<BetRecord>; //For BJ use
};

type GameResultProps = {
    title: string;
    iconButton?: JSX.Element;
    summary: Summary;
    withDivider?: boolean;
    betRecords?: Array<BetRecord>; //For BJ use
};

const GameResult = (props: GameResultProps) => {
    const {
        title,
        iconButton,
        summary,
        withDivider = false,
        betRecords,
    } = props;

    const { t } = useTranslation();

    const [resultPanel, setResultPanel] = useState<JSX.Element>(<></>);
    const [resultBelowTitle, setResultBelowTitle] = useState<boolean>(false);
    const [isExpandable, setIsExpandable] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);

    const handleToggleExpand = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        if (summary.State == 0 || summary.WinSlot == 0) {
            // on going
            return setResultPanel(
                <Box className="item-on-going-result-container">
                    {t('betlog.on_going')}
                </Box>
            );
        } else {
            switch (summary.GameType) {
                case GameTypeEnum.Baccarat:
                case GameTypeEnum.SqueezeBaccarat:
                    return setResultPanel(<BaccGameResult summary={summary} />);
                case GameTypeEnum.SeDie:
                    return setResultPanel(
                        <SedieGameResult summary={summary} />
                    );
                case GameTypeEnum.PokDeng:
                    setResultBelowTitle(true);
                    return setResultPanel(
                        <PokDengGameResult summary={summary} />
                    );
                case GameTypeEnum.Dragon:
                    return setResultPanel(
                        <DragonTigerGameResult summary={summary} />
                    );
                case GameTypeEnum.SicBo:
                case GameTypeEnum.ThaiHiLo:
                    return setResultPanel(
                        <SicBoGameResult summary={summary} />
                    );
                case GameTypeEnum.TeenPatti2020:
                    setResultBelowTitle(true);
                    setIsExpandable(true);
                    return setResultPanel(
                        <TeenPattiGameResult
                            summary={summary}
                            expanded={expanded}
                        />
                    );
                case GameTypeEnum.AndarBahar:
                    setResultBelowTitle(true);
                    return setResultPanel(
                        <AndarBaharGameResult summary={summary} />
                    );
                case GameTypeEnum.Roulette:
                    return setResultPanel(
                        <RouletteGameResult summary={summary} />
                    );
                case GameTypeEnum.UltraRoulette:
                    return setResultPanel(
                        <UltraRouletteGameResult summary={summary} />
                    );
                case GameTypeEnum.Blackjack:
                    setResultBelowTitle(true);
                    setIsExpandable(true);
                    return setResultPanel(
                        <BlackjackGameResult
                            betRecords={betRecords}
                            summary={summary}
                            expanded={expanded}
                        />
                    );
                case GameTypeEnum.FishPrawnCrab:
                    return setResultPanel(
                        <FishPrawnCrabGameResult summary={summary} />
                    );
                case GameTypeEnum.CarnivalTreasure:
                    setIsExpandable(
                        (
                            convertDetailResultString(
                                summary.Result
                            ) as CarnivalTreasureResult
                        ).MultiplierSize > 0
                    );
                    setResultBelowTitle(true);
                    return setResultPanel(
                        <CarnivalTreasureGameResult
                            summary={summary}
                            expanded={expanded}
                        />
                    );
                case GameTypeEnum.Blackjack2:
                    setResultBelowTitle(true);
                    setIsExpandable(
                        betRecords !== undefined && betRecords.length > 0
                    );
                    return setResultPanel(
                        <Blackjack2GameResult
                            betRecords={betRecords}
                            summary={summary}
                            expanded={expanded}
                        />
                    );
                default:
                    return setResultPanel(<></>);
            }
        }
    }, [summary, expanded, betRecords]);

    return (
        <>
            <ListItem
                className={`game-result-list-item ${resultBelowTitle ? 'result-below-title' : ''}`}
            >
                <ListItemText>
                    <div className="game-result-list-item-text-stack">
                        <Typography className="detail-list-item-text-font">
                            {title}
                        </Typography>
                        {isExpandable && (
                            <IconButton
                                id={
                                    expanded
                                        ? ''
                                        : 'btn_set-br_game-result-expand'
                                }
                                onClick={handleToggleExpand}
                                className="icon-container"
                                sx={{
                                    color: '#BF9A5D',
                                }}
                            >
                                {expanded ? (
                                    <RemoveCircleOutlineIcon />
                                ) : (
                                    <ControlPointIcon />
                                )}
                            </IconButton>
                        )}
                        {iconButton}
                    </div>
                </ListItemText>
                {resultPanel}
            </ListItem>
            {withDivider && <div className="game-result-divider" />}
        </>
    );
};

export default GameResult;
